import { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from './ui/dialog';
import { Bookmark, ChevronDown, Plus, Trash2, Check, Star } from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '../lib/utils';

/**
 * Smart Filters Component
 * 
 * Usage:
 * <SmartFilters 
 *   pageKey="tickets"  // Unique key for this page's filters
 *   currentFilters={{ stato: 'aperto', tecnico: 'abc123' }}
 *   onApplyFilter={(filters) => setFilters(filters)}
 *   filterLabels={{ stato: 'Stato', tecnico: 'Tecnico' }}
 * />
 */
export default function SmartFilters({ 
  pageKey, 
  currentFilters = {}, 
  onApplyFilter, 
  filterLabels = {},
  className 
}) {
  const [savedFilters, setSavedFilters] = useState([]);
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [newFilterName, setNewFilterName] = useState('');
  const [activePreset, setActivePreset] = useState(null);

  // Load saved filters from localStorage
  useEffect(() => {
    const stored = localStorage.getItem(`smartFilters_${pageKey}`);
    if (stored) {
      try {
        setSavedFilters(JSON.parse(stored));
      } catch (e) {
        console.error('Error loading saved filters:', e);
      }
    }
  }, [pageKey]);

  // Save filters to localStorage
  const persistFilters = (filters) => {
    localStorage.setItem(`smartFilters_${pageKey}`, JSON.stringify(filters));
    setSavedFilters(filters);
  };

  // Check if current filters match a preset
  useEffect(() => {
    const matching = savedFilters.find(f => {
      const filterKeys = Object.keys(f.filters);
      const currentKeys = Object.keys(currentFilters).filter(k => currentFilters[k]);
      
      if (filterKeys.length !== currentKeys.length) return false;
      
      return filterKeys.every(key => f.filters[key] === currentFilters[key]);
    });
    
    setActivePreset(matching?.id || null);
  }, [currentFilters, savedFilters]);

  // Count active filters
  const activeFilterCount = Object.values(currentFilters).filter(v => v && v !== 'all').length;

  // Save current filters as preset
  const handleSaveFilter = () => {
    if (!newFilterName.trim()) {
      toast.error('Inserisci un nome per il filtro');
      return;
    }

    // Only save non-empty filters
    const filtersToSave = {};
    Object.entries(currentFilters).forEach(([key, value]) => {
      if (value && value !== 'all' && value !== '') {
        filtersToSave[key] = value;
      }
    });

    if (Object.keys(filtersToSave).length === 0) {
      toast.error('Nessun filtro attivo da salvare');
      return;
    }

    const newFilter = {
      id: Date.now().toString(),
      name: newFilterName.trim(),
      filters: filtersToSave,
      createdAt: new Date().toISOString(),
      isDefault: savedFilters.length === 0 // First one becomes default
    };

    persistFilters([...savedFilters, newFilter]);
    setNewFilterName('');
    setSaveDialogOpen(false);
    toast.success(`Filtro "${newFilter.name}" salvato`);
  };

  // Apply a saved filter
  const handleApplyFilter = (filter) => {
    onApplyFilter(filter.filters);
    toast.info(`Filtro "${filter.name}" applicato`);
  };

  // Delete a saved filter
  const handleDeleteFilter = (filterId, e) => {
    e.stopPropagation();
    const updated = savedFilters.filter(f => f.id !== filterId);
    persistFilters(updated);
    toast.success('Filtro eliminato');
  };

  // Set as default filter
  const handleSetDefault = (filterId, e) => {
    e.stopPropagation();
    const updated = savedFilters.map(f => ({
      ...f,
      isDefault: f.id === filterId
    }));
    persistFilters(updated);
    toast.success('Filtro impostato come predefinito');
  };

  // Clear all filters
  const handleClearFilters = () => {
    const clearedFilters = {};
    Object.keys(currentFilters).forEach(key => {
      clearedFilters[key] = '';
    });
    onApplyFilter(clearedFilters);
    setActivePreset(null);
  };

  // Get readable label for filter value
  const getFilterLabel = (key, value) => {
    if (!value || value === 'all') return null;
    const label = filterLabels[key] || key;
    return `${label}: ${value}`;
  };

  return (
    <div className={cn("flex items-center gap-2", className)}>
      {/* Active filter badges */}
      {activeFilterCount > 0 && (
        <div className="hidden md:flex items-center gap-1 flex-wrap">
          {Object.entries(currentFilters).map(([key, value]) => {
            const label = getFilterLabel(key, value);
            if (!label) return null;
            return (
              <Badge 
                key={key} 
                variant="secondary" 
                className="text-xs rounded-md px-2 py-1 bg-cyan-500/10 text-cyan-600 border-cyan-500/30"
              >
                {label}
              </Badge>
            );
          })}
        </div>
      )}

      {/* Saved Filters Dropdown */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button 
            variant="outline" 
            size="sm" 
            className={cn(
              "rounded-md h-9 gap-1",
              activePreset && "border-cyan-500 bg-cyan-500/10"
            )}
            data-testid="smart-filters-btn"
          >
            <Bookmark className={cn("h-4 w-4", activePreset && "text-cyan-500 fill-cyan-500")} />
            <span className="hidden sm:inline">
              {activePreset 
                ? savedFilters.find(f => f.id === activePreset)?.name 
                : 'Filtri'}
            </span>
            {!activePreset && activeFilterCount > 0 && (
              <Badge variant="secondary" className="ml-1 h-5 w-5 p-0 justify-center rounded-full text-[10px]">
                {activeFilterCount}
              </Badge>
            )}
            <ChevronDown className="h-3 w-3 ml-1" />
          </Button>
        </DropdownMenuTrigger>
        
        <DropdownMenuContent align="end" className="w-64">
          {/* Saved presets */}
          {savedFilters.length > 0 && (
            <>
              <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground">
                Filtri Salvati
              </div>
              {savedFilters.map((filter) => (
                <DropdownMenuItem
                  key={filter.id}
                  onClick={() => handleApplyFilter(filter)}
                  className={cn(
                    "cursor-pointer flex items-center justify-between group",
                    activePreset === filter.id && "bg-cyan-500/10"
                  )}
                >
                  <div className="flex items-center gap-2 flex-1 min-w-0">
                    {filter.isDefault && (
                      <Star className="h-3 w-3 text-amber-500 fill-amber-500 shrink-0" />
                    )}
                    {activePreset === filter.id && !filter.isDefault && (
                      <Check className="h-3 w-3 text-cyan-500 shrink-0" />
                    )}
                    <span className="truncate">{filter.name}</span>
                    <span className="text-[10px] text-muted-foreground ml-1">
                      ({Object.keys(filter.filters).length})
                    </span>
                  </div>
                  <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    {!filter.isDefault && (
                      <button
                        onClick={(e) => handleSetDefault(filter.id, e)}
                        className="p-1 hover:bg-muted rounded"
                        title="Imposta come predefinito"
                      >
                        <Star className="h-3 w-3" />
                      </button>
                    )}
                    <button
                      onClick={(e) => handleDeleteFilter(filter.id, e)}
                      className="p-1 hover:bg-destructive/10 rounded text-destructive"
                      title="Elimina"
                    >
                      <Trash2 className="h-3 w-3" />
                    </button>
                  </div>
                </DropdownMenuItem>
              ))}
              <DropdownMenuSeparator />
            </>
          )}
          
          {/* Save current */}
          {activeFilterCount > 0 && !activePreset && (
            <DropdownMenuItem
              onClick={() => setSaveDialogOpen(true)}
              className="cursor-pointer text-cyan-600"
            >
              <Plus className="h-4 w-4 mr-2" />
              Salva filtro corrente
            </DropdownMenuItem>
          )}
          
          {/* Clear filters */}
          {activeFilterCount > 0 && (
            <DropdownMenuItem
              onClick={handleClearFilters}
              className="cursor-pointer text-muted-foreground"
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Cancella filtri
            </DropdownMenuItem>
          )}
          
          {/* Empty state */}
          {savedFilters.length === 0 && activeFilterCount === 0 && (
            <div className="px-2 py-4 text-center text-sm text-muted-foreground">
              <Bookmark className="h-8 w-8 mx-auto mb-2 opacity-50" />
              <p>Nessun filtro salvato</p>
              <p className="text-xs">Applica dei filtri e salvali qui</p>
            </div>
          )}
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Save Dialog */}
      <Dialog open={saveDialogOpen} onOpenChange={setSaveDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Salva Filtro</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Input
                placeholder="Nome del filtro (es: Ticket urgenti)"
                value={newFilterName}
                onChange={(e) => setNewFilterName(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSaveFilter()}
                autoFocus
                data-testid="filter-name-input"
              />
            </div>
            <div className="text-sm text-muted-foreground">
              <p className="font-medium mb-2">Filtri da salvare:</p>
              <div className="flex flex-wrap gap-1">
                {Object.entries(currentFilters).map(([key, value]) => {
                  const label = getFilterLabel(key, value);
                  if (!label) return null;
                  return (
                    <Badge key={key} variant="outline" className="text-xs">
                      {label}
                    </Badge>
                  );
                })}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSaveDialogOpen(false)}>
              Annulla
            </Button>
            <Button onClick={handleSaveFilter} data-testid="save-filter-btn">
              Salva
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
